<script setup lang="ts">
const props = defineProps({
  upvoteCount: { type: Number, required: true },
  downvoteCount: { type: Number, required: true },
  comments: { type: Number, required: true },
  isUpvoted: { type: Boolean, required: true },
  isDownvoted: { type: Boolean, required: true },
  isFollow: { type: Boolean, required: true },
  isAuthor: { type: Boolean, required: true },
  isAdmin: { type: Boolean, required: true },
  isAuthenticated: { type: Boolean, required: true },
  postId: { type: String, required: true },
  userId: { type: String, required: true },
  isResolved: { type: Boolean, required: true },
  isAnnouncement: { type: Boolean, required: true }
})

// --------------------
// VIEW MODEL
// --------------------

const showUnfollowMenu = ref(false)

const emit = defineEmits(['refresh'])

// --------------------
// FORUM-BACKEND
// --------------------

const { followPost, unfollowPost } = useForumFollowedPost()
const { updatePostVote } = useForumVote()

const clickFollow = () => {
  if (props.isAuthenticated) followPost(props.postId)
  emit('refresh')
}

const clickUnfollow = () => {
  showUnfollowMenu.value = false
  if (props.isAuthenticated) unfollowPost(props.postId)
  emit('refresh')
}

const clickUpvote = async () => {
  if (props.isAuthenticated) {
    if (props.isUpvoted) {
      // check whether user has already upvoted the post, click upvote button again will cancel the vote
      await updatePostVote(
        props.userId,
        { post_id_slug: props.postId, vote_value: 0 },
        'remove_vote'
      )
    } else {
      // otherwise, upvote the post
      await updatePostVote(props.userId, { post_id_slug: props.postId, vote_value: 1 }, 'upvote')
    }
    // emit refresh event
    emit('refresh')
  } else {
    alert('You have to be logged in to vote.')
  }
}

const clickDownvote = async () => {
  if (props.isAuthenticated) {
    if (props.isDownvoted) {
      // check whether user has already downvoted the post, click downvote button again will cancel the vote
      await updatePostVote(
        props.userId,
        { post_id_slug: props.postId, vote_value: 0 },
        'remove_vote'
      )
    } else {
      // otherwise, downvote the post
      await updatePostVote(props.userId, { post_id_slug: props.postId, vote_value: -1 }, 'downvote')
    }
    // emit refresh event
    emit('refresh')
  } else {
    alert('You have to be logged in to vote.')
  }
}
</script>

<template>
  <div class="post-card-footer">
    <PageForumPostStats
      :upvote-count="upvoteCount"
      :downvote-count="downvoteCount"
      :comments="comments"
      :is-upvoted="isUpvoted"
      :is-downvoted="isDownvoted"
      @click-upvote="clickUpvote"
      @click-downvote="clickDownvote"
      @click-comment="navigateTo(`/forum/post/${postId}#comments-list`)"
    >
      <!-- Only admin can follow post directly -->
      <div
        v-if="isAuthenticated && !isAuthor && isAdmin && isFollow && !isResolved && !isAnnouncement"
        class="following-btn"
        @click.stop="showUnfollowMenu = !showUnfollowMenu"
      >
        <CommonButton extra-small grey>
          <span>Following</span>
          <CommonIconChevron />
        </CommonButton>

        <CommonMenuDropdown :show-menu="showUnfollowMenu" @close-menu="showUnfollowMenu = false">
          <div @click.stop="clickUnfollow">Unfollow post</div>
        </CommonMenuDropdown>
      </div>

      <!-- Only admin can unfollow post directly -->
      <div
        v-if="
          isAuthenticated && !isAuthor && isAdmin && !isFollow && !isResolved && !isAnnouncement
        "
        @click.stop="clickFollow"
      >
        <CommonButton extra-small outline>
          <CommonIconAdd />
          <span>Follow post</span>
        </CommonButton>
      </div>
    </PageForumPostStats>
  </div>
</template>

<style scoped lang="scss">
.following-btn {
  position: relative;

  :deep(.btn-xs) {
    padding-right: 10px;

    svg {
      transform: rotate(90deg);
      margin-top: 3px;
      height: 10px;
      width: 10px;
    }
  }
}
</style>
